<template>
  <div
    class="h-screen overflow-scroll w-full md:w-96 lg:w-64 xl:w-80 fixed top-0 bg-white right-0 z-30 border b-l"
  >
    <div class="px-5 py-7 border-b flex justify-center items-center">
      <button @click="$emit('close')" class="absolute left-5 text-xs">
        Close
      </button>
      <h1 class="text-yank font-medium w-32 truncate text-center">
        {{ getName }}
      </h1>
    </div>
    <div class="px-5 py-3">
      <div class="w-full h-48 rounded-xl mb-6">
        <div v-if="includesPdf" class="h-full">
            <img
                src="images/pdf.svg"
                :alt="selectedMedia.name"
                loading="lazy"
                class="w-full h-full object-cover"
            />
        </div>
        <img
          v-else-if="!includesVideo"
          :src="selectedMedia.url"
          :onerror="`this.onerror=null; this.src='${IMAGE_PLACEHOLDER}'`"
          :alt="selectedMedia.name"
          class="object-cover rounded-xl w-full h-48"
        />
        <video
          ref="video"
          v-else
          class="object-cover rounded-xl w-full h-48"
          autoplay
          muted
          loop
          :poster="CHARGING_VIDEO"
        >
          <source
            :src="selectedMedia.url"
            :type="
              selectedMedia.mime_type &&
              selectedMedia.mime_type !== 'video/quicktime'
                ? selectedMedia.mime_type
                : 'video/mp4'
            "
          />
          Your browser does not support the video tag.
        </video>
      </div>
      <div>
        <label> Name </label>
        <div class="mt-1 shadow-sm rounded-xl relative">
          <div class="absolute right-2 top-2">
            <svg
              v-if="updating === 'name'"
              class="animate-spin h-6 w-6"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          </div>
          <input
            type="text"
            name="name"
            class="form-input"
            v-model="actualName"
          />
        </div>
      </div>
      <div>
        <label> Description </label>
        <div
          class="mt-1 shadow-sm rounded-xl h-36 border border-payneGringo text-gray-600 relative"
        >
          <div class="absolute right-2 top-2">
            <svg
              v-if="updating === 'description'"
              class="animate-spin h-6 w-6"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          </div>
          <textarea
            class="w-full h-full border-none resize-none rounded-xl"
            name="textarea"
            rows="10"
            cols="50"
            v-model="actualDescription"
          />
        </div>
      </div>
      <div class="space-y-3">
        <div>
          <h2>External Link</h2>
          <a :href="selectedMedia.url">{{
            selectedMedia.url ? selectedMedia.url.slice(0, 70) + "..." : ""
          }}</a>
        </div>
        <div>
          <h2>File Type</h2>
          <span>{{ getType }}</span>
        </div>
        <div>
          <h2>File Size</h2>
          <span>{{ selectedMedia.size }}</span>
        </div>
        <div>
          <h2>Dimensions</h2>
          <span>{{ getImageDimensions }}</span>
        </div>
        <button
          class="text-white font-medium w-full bg-carolina px-3 py-2 text-sm rounded-lg mr-2 mb-2 md:mb-0"
          type="button"
          @click="showMoveModal = true"
        >
          Move
        </button>
        <button
          @click="download"
          class="text-white font-medium w-full bg-green-300 px-3 py-2 text-sm rounded-lg mr-2 mb-2 md:mb-0"
        >
          <a
            dusk="download-button"
            class="btn-download"
            :href="
              $route('download', {
                type: 'media',
                uuid: selectedMedia.files[0].uuid,
              })
            "
          >
            Download
          </a>
        </button>
        <button
          class="text-white font-medium w-full bg-red-400 px-3 py-2 text-sm rounded-lg mr-2 mb-2 md:mb-0"
          type="button"
          @click="deleteItem"
        >
          Delete
        </button>
      </div>
    </div>
    <portal to="destination">
      <div
        v-if="showMoveModal"
        class="flex justify-center items-center fixed z-20 bg-gray-500 bg-opacity-30 h-screen w-full"
      >
        <MoveItemModal
          v-on="$listeners"
          @removeFromRoot="removeFromRoot"
          @closeModal="showMoveModal = false"
          :item="selectedMedia"
        />
      </div>
    </portal>
  </div>
</template>

<script>
import axios from "axios";
import _ from "lodash";
import { IMAGE_PLACEHOLDER, CHARGING_VIDEO } from "@/utils/constants";
import MoveItemModal from "@/components/my-media/MoveItemModal.vue";

export default {
  name: "MediaDetails",

  props: {
    selectedMedia: Object,
    flash: Object,
  },
  components: {
    MoveItemModal,
  },

  data() {
    return {
      actualName: "",
      actualDescription: "",
      debounce: null,
      updating: "",
      IMAGE_PLACEHOLDER,
      CHARGING_VIDEO,
      showMoveModal: false,
    };
  },

  computed: {
    includesPdf() {
      return (
        this.selectedMedia &&
        this.selectedMedia.mime_type &&
        this.selectedMedia.mime_type.includes("pdf")
      );
    },
    includesVideo() {
      return (
        this.selectedMedia &&
        this.selectedMedia.mime_type &&
        this.selectedMedia.mime_type.includes("video")
      );
    },
    getName() {
      return this.selectedMedia.name.split(".")[0];
    },
    getType() {
      return this.selectedMedia.mime_type.split("/")[1];
    },
    getImageDimensions() {
      return this.selectedMedia.dimensions
        ? this.selectedMedia.dimensions.width +
            " x " +
            this.selectedMedia.dimensions.height
        : "-";
    },
  },

  mounted() {
    this.actualName = this.getName;
    this.actualDescription = this.selectedMedia.description;
  },

  methods: {
    deleteItem() {
      this.$emit("deleteItem", this.selectedMedia);
      this.$emit("close");
    },

    download() {
      this.$toast.success("The download will start shortly.");
    },

    saveChanges(updatingType) {
      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => {
        if (this.actualName && this.actualName !== "") {
          this.updating = updatingType;
          try {
            axios
              .put(
                this.$route("church-media.items.update", this.selectedMedia.id),
                {
                  name: this.actualName,
                  description: this.actualDescription,
                  redirect: false,
                }
              )
              .then((response) => {
                this.selectedMedia.name = response.data.name;
                this.selectedMedia.description = response.data.description;
                this.updating = "";
              });
          } catch (ex) {}
        }
      }, 500);
    },

    removeFromRoot(item) {
      this.$emit("removeFromRoot", item);
    },
  },

  watch: {
    actualName(name) {
      this.saveChanges("name");
    },

    selectedMedia(value) {
      this.actualName = this.getName;
      this.actualDescription = this.selectedMedia.description;
    },

    actualDescription(description) {
      this.saveChanges("description");
    },
  },
};
</script>

<style scoped>
.form-input {
  @apply h-13 px-4 text-sm text-gray-600 border border-payneGringo rounded-xl w-full focus:border-yank transition-colors focus:outline-none;
}
label {
  @apply block text-yank font-medium text-sm;
}
h2 {
  @apply text-cosmosFire text-xs font-medium;
}
span {
  @apply text-yank text-xs;
}
a {
  @apply text-carolina text-xs;
}

.btn-download {
  color: white !important;
  font-size: 100% !important;
}
</style>
